import React from 'react';
import { DollarSign, Clock } from 'lucide-react';
import type { ClosedPnL } from '../../lib/types';
import { MetricCard } from './MetricCard';
import { formatCurrency } from './formatters';

interface TradingFeesSummaryProps {
  closedTrades: Array<ClosedPnL & { openingFee?: string; closingFee?: string }>;
}

export const TradingFeesSummary: React.FC<TradingFeesSummaryProps> = ({ closedTrades }) => {
  const openingFees = closedTrades.reduce((sum, trade) => sum + Number.parseFloat(trade.openingFee || '0'), 0);
  const closingFees = closedTrades.reduce((sum, trade) => sum + Number.parseFloat(trade.closingFee || '0'), 0);
  const totalFees = openingFees + closingFees;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Trading Fees Summary</h2>
        <p className="text-sm text-gray-600 mt-1">Aggregate trading fees across all traders</p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Opening Fees"
            value={formatCurrency(openingFees)}
            icon={<DollarSign className="h-6 w-6" />}
            description="Sum of all opening fees"
          />
          <MetricCard
            title="Total Closing Fees"
            value={formatCurrency(closingFees)}
            icon={<DollarSign className="h-6 w-6" />}
            description="Sum of all closing fees"
          />
          <MetricCard
            title="Funding Fees"
            value="Not Tracked"
            icon={<Clock className="h-6 w-6" />}
            description="Funding rates applied every 8h (separate from trade P&L)"
          />
          <MetricCard
            title="Total Fees"
            value={formatCurrency(totalFees)}
            icon={<DollarSign className="h-6 w-6" />}
            trend="neutral"
            description="Opening + Closing fees"
          />
        </div>
      </div>
    </div>
  );
};
